import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useAuth } from '../../contexts/AuthContext';
import TopBarActions from '../../components/layout/TopBarActions';
import IdentityCarousel from '../../components/IdentityCarousel';

/** Mesma linguagem visual do ARCA / shell SGC. */
const PALETTE = {
  bg: {
    shell: 'linear-gradient(180deg, #f8fafc 0%, #e3f2fd 100%)',
    drawer: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
    header: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 40%, #00897b 100%)',
  },
  text: {
    primary: '#0d47a1',
    nav: '#37474f',
    muted: '#64748b',
  },
  accent: { main: '#1976d2' },
  border: { light: 'rgba(25,118,210,0.12)', divider: 'rgba(25,118,210,0.1)' },
  hover: { navItem: 'rgba(25,118,210,0.06)' },
};

const DRAWER_W = 240;

const NAV_ITEMS_BASE = [
  { label: 'Dashboard', path: '/safety-huddle/dashboard', icon: <DashboardIcon />, exact: true },
  {
    label: 'Visão corporativa',
    path: '/safety-huddle/corporativo',
    icon: <AccountTreeIcon />,
    exact: true,
    corporateOnly: true,
  },
  { label: 'Setores', path: '/safety-huddle', icon: <CategoryIcon />, exact: true },
  {
    label: 'Admin setores',
    path: '/safety-huddle/admin/setores',
    icon: <SettingsIcon />,
    exact: true,
    adminOnly: true,
  },
];

function isActive(pathname, item) {
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function SafetySidebar({ location, onNavigate, onCloseMobile, unidadeLabel, showAdmin, showCorporate }) {
  const navItems = NAV_ITEMS_BASE.filter((item) => {
    if (item.adminOnly && !showAdmin) return false;
    if (item.corporateOnly && !showCorporate) return false;
    return true;
  });
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: PALETTE.bg.drawer,
        backdropFilter: 'blur(10px)',
        color: PALETTE.text.nav,
      }}
    >
      <Box
        sx={{
          minHeight: 88,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: PALETTE.bg.header,
          borderRadius: '0 20px 0 0',
          position: 'relative',
          overflow: 'hidden',
          px: 2,
          py: 2,
          boxShadow: '0 8px 32px 0 rgba(0,137,123,0.12), 0 1.5px 8px 0 rgba(25,118,210,0.10)',
        }}
      >
        <HealthAndSafetyIcon sx={{ color: '#fff', fontSize: 28, mb: 0.5, zIndex: 1 }} />
        <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 2, fontSize: '0.65rem', zIndex: 1 }}>
          Módulo
        </Typography>
        <Typography variant="h6" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, zIndex: 1, textAlign: 'center' }}>
          Safety Huddle
        </Typography>
      </Box>

      {unidadeLabel && (
        <Box sx={{ px: 2, py: 1.25 }}>
          <Chip
            label={unidadeLabel}
            size="small"
            sx={{
              width: '100%',
              fontWeight: 700,
              color: 'primary.main',
              bgcolor: 'rgba(25,118,210,0.08)',
              border: '1px solid rgba(25,118,210,0.2)',
              borderRadius: 2,
            }}
          />
        </Box>
      )}

      <Divider sx={{ borderColor: PALETTE.border.divider }} />

      <List sx={{ px: 1, py: 1.5, flex: 1, overflow: 'auto' }}>
        {navItems.map((item) => {
          const active = isActive(location.pathname, item);
          return (
            <ListItemButton
              key={item.path}
              selected={active}
              onClick={() => {
                onNavigate(item.path);
                onCloseMobile();
              }}
              sx={{
                borderRadius: 3,
                mb: 0.5,
                bgcolor: active ? 'rgba(25,118,210,0.1)' : 'transparent',
                '&:hover': { bgcolor: PALETTE.hover.navItem, transform: 'translateX(4px)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? PALETTE.accent.main : 'primary.main' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: active ? 700 : 600, fontSize: '0.9rem' }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: PALETTE.border.divider }} />

      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={() => {
            onNavigate('/home');
            onCloseMobile();
          }}
          sx={{
            borderRadius: 3,
            color: PALETTE.text.muted,
            border: `1px solid ${PALETTE.border.light}`,
            '&:hover': {
              bgcolor: PALETTE.hover.navItem,
              color: PALETTE.accent.main,
              transform: 'translateX(4px)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <ArrowBackIcon />
          </ListItemIcon>
          <ListItemText
            primary="Voltar ao Painel"
            primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );
}

function getPageTitle(pathname) {
  if (pathname === '/safety-huddle/dashboard') return 'Dashboard';
  if (pathname === '/safety-huddle/corporativo') return 'Visão corporativa';
  if (pathname === '/safety-huddle/admin/setores') return 'Admin · Setores';
  if (pathname.includes('/historico/')) return 'Histórico';
  if (pathname.includes('/admin/')) return 'Configurar formulário';
  if (pathname.includes('/dashboard/')) return 'Dashboard do formulário';
  if (pathname.includes('/formularios/')) return 'Formulário';
  if (pathname.includes('/setores/')) return 'Setor';
  return 'Safety Huddle';
}

export default function SafetyHuddleLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { unidadeSelecionada, isAdmin, rbacRole, isCargoCorporativo, user } = useAuth();
  const unidadeLabel = unidadeSelecionada?.nome || unidadeSelecionada?.codigo || '';
  const unidadeCodigo = unidadeSelecionada?.codigo || '';
  const title = getPageTitle(location.pathname);
  const prevUnidadeRef = useRef(unidadeCodigo);
  const showAdmin =
    isAdmin ||
    ['SUPER_ADMIN', 'LOCAL_ADMIN', 'LOCAL_APPROVER'].includes(rbacRole);
  const showCorporate =
    isAdmin ||
    isCargoCorporativo ||
    Boolean(user?.canAccessAllUnidades) ||
    ['SUPER_ADMIN', 'CORPORATE_MANAGER', 'GLOBAL_VIEWER'].includes(rbacRole);

  // Troca de unidade: volta ao hub para não manter IDs de outra unidade na URL
  useEffect(() => {
    const prev = prevUnidadeRef.current;
    if (prev && unidadeCodigo && prev !== unidadeCodigo) {
      const deep =
        location.pathname.includes('/setores/') ||
        location.pathname.includes('/formularios/') ||
        location.pathname.includes('/admin/') ||
        /\/dashboard\/\d+/.test(location.pathname);
      if (deep) {
        navigate('/safety-huddle', { replace: true });
      }
    }
    prevUnidadeRef.current = unidadeCodigo;
  }, [unidadeCodigo, location.pathname, navigate]);

  const drawerPaperSx = {
    width: DRAWER_W,
    border: 0,
    boxSizing: 'border-box',
    background: PALETTE.bg.drawer,
    borderRadius: '0 20px 20px 0',
    boxShadow: '0 8px 32px rgba(25,118,210,0.12)',
  };

  const sidebar = (
    <SafetySidebar
      location={location}
      onNavigate={navigate}
      onCloseMobile={() => setMobileOpen(false)}
      unidadeLabel={unidadeLabel}
      showAdmin={showAdmin}
      showCorporate={showCorporate}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        height: '100vh',
        background: PALETTE.bg.shell,
        overflow: 'hidden',
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Drawer variant="permanent" sx={{ width: DRAWER_W, flexShrink: 0, '& .MuiDrawer-paper': drawerPaperSx }} open>
          {sidebar}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          pt: { xs: 1, sm: 1.5 },
          boxSizing: 'border-box',
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
            backdropFilter: 'blur(10px)',
            color: '#1976d2',
            mb: 0,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderBottom: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '0 0 20px 20px',
            flexShrink: 0,
          }}
        >
          <Toolbar sx={{ flexWrap: 'wrap', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 }, gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} color="primary" aria-label="Abrir menu">
                <MenuIcon />
              </IconButton>
            )}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/home')}
              size="small"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': { background: 'rgba(25, 118, 210, 0.1)' },
              }}
            >
              Voltar ao Painel
            </Button>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {title}
            </Typography>
            <TopBarActions />
            <IconButton
              onClick={() => window.dispatchEvent(new CustomEvent('refreshPage'))}
              aria-label="Atualizar"
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ mt: 2, flexShrink: 0 }}>
          <IdentityCarousel />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, mt: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
